import { ref, watch, computed } from '@vue/composition-api'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import Vue from 'vue'
import apiEndpoints from '@/constants/apiEndpoints'
import router from '../../../../router'

export default function usePurchaseRequestsList() {
  const toast = useToast()

  // ------------------------------------------------
  // REFS: Tables
  // ------------------------------------------------
  const refPurchaseRequestListTableAssetsFGS = ref(null)
  const refPurchaseRequestListTableSupplies = ref(null)
  const refPurchaseRequestListTableHO = ref(null)
  const refPurchaseRequestListTableSuppliesNA = ref(null)
  const refPurchaseRequestListTableAssetsFGSNA = ref(null)
  const refPurchaseRequestListTableHONA = ref(null)
  const refPurchaseRequestListTableFinishedAssetsFGS = ref(null)
  const refPurchaseRequestListTableFinishedSupplies = ref(null)
  const refPurchaseRequestListTableFinishedHO = ref(null)
  const refPurchaseRequestListTableVoid = ref(null)

  // ------------------------------------------------
  // SHARED CONSTANTS: Columns & Options
  // ------------------------------------------------
  const perPageOptions = [10, 25, 50, 100]

  const standardColumns = [
    { key: 'Number', sortable: true },
    { key: 'Class', sortable: true },
    { key: 'Category', sortable: true },
    { key: 'Type', sortable: true },
    { key: 'CostCenter', sortable: true },
    { key: 'Notes', sortable: true },
    { key: 'DateCreated', sortable: true },
    { key: 'actions' },
  ]

  const tableColumnsAssetsFGS = [...standardColumns]
  const tableColumnsAssetsFGSNA = [...standardColumns]
  const tableColumnsFinishedAssetsFGS = [...standardColumns]
  const tableColumnsSupplies = [...standardColumns]
  const tableColumnsSuppliesNA = [...standardColumns]
  const tableColumnsFinishedSupplies = [...standardColumns]
  const tableColumnsHO = [...standardColumns]
  const tableColumnsHONA = [...standardColumns]
  const tableColumnsFinishedHO = [...standardColumns]
  const tableColumnsFinished = [...standardColumns]

  const tableColumnsVoid = [
    ...standardColumns.slice(0, 7),
    { key: 'VoidBy', sortable: false },
    { key: 'VoidTime', sortable: false },
    { key: 'actions' },
  ]

  // ------------------------------------------------
  // REFS: Pagination, Sorting, Filters
  // ------------------------------------------------
  // Assets FGS
  const perPageAssetsFGS = ref(10)
  const fileFilter = ref('all')
  const currentPageAssetsFGS = ref(1)
  const totalPurchaseRequestsAssetsFGS = ref(0)
  const searchQueryAssetsFGS = ref('')
  const sortByAssetsFGS = ref('id')
  const isSortDirDescAssetsFGS = ref(true)
  const startDateFilterAssetsFGS = ref('')
  const endDateFilterAssetsFGS = ref('')

  // Assets FGS - NA
  const perPageAssetsFGSNA = ref(10)
  const currentPageAssetsFGSNA = ref(1)
  const totalPurchaseRequestsAssetsFGSNA = ref(0)
  const searchQueryAssetsFGSNA = ref('')
  const sortByAssetsFGSNA = ref('id')
  const isSortDirDescAssetsFGSNA = ref(true)
  const startDateFilterAssetsFGSNA = ref('')
  const endDateFilterAssetsFGSNA = ref('')

  // Assets FGS - Finished
  const perPageFinishedAssetsFGS = ref(10)
  const currentPageFinishedAssetsFGS = ref(1)
  const totalPurchaseRequestsFinishedAssetsFGS = ref(0)
  const searchQueryFinishedAssetsFGS = ref('')
  const startDateFilterFinishedAssetsFGS = ref('')
  const endDateFilterFinishedAssetsFGS = ref('')

  // Supplies
  const perPageSupplies = ref(10)
  const currentPageSupplies = ref(1)
  const totalPurchaseRequestsSupplies = ref(0)
  const searchQuerySupplies = ref('')
  const sortBySupplies = ref('id')
  const isSortDirDescSupplies = ref(true)
  const startDateFilterSupplies = ref('')
  const endDateFilterSupplies = ref('')

  // Supplies - NA
  const perPageSuppliesNA = ref(10)
  const currentPageSuppliesNA = ref(1)
  const totalPurchaseRequestsSuppliesNA = ref(0)
  const searchQuerySuppliesNA = ref('')
  const sortBySuppliesNA = ref('id')
  const isSortDirDescSuppliesNA = ref(true)
  const startDateFilterSuppliesNA = ref('')
  const endDateFilterSuppliesNA = ref('')

  // Supplies - Finished
  const perPageFinishedSupplies = ref(10)
  const currentPageFinishedSupplies = ref(1)
  const totalPurchaseRequestsFinishedSupplies = ref(0)
  const searchQueryFinishedSupplies = ref('')
  const startDateFilterFinishedSupplies = ref('')
  const endDateFilterFinishedSupplies = ref('')

  // HO
  const perPageHO = ref(10)
  const currentPageHO = ref(1)
  const totalPurchaseRequestsHO = ref(0)
  const searchQueryHO = ref('')
  const sortByHO = ref('id')
  const isSortDirDescHO = ref(true)
  const startDateFilterHO = ref('')
  const endDateFilterHO = ref('')

  // HO - NA
  const perPageHONA = ref(10)
  const currentPageHONA = ref(1)
  const totalPurchaseRequestsHONA = ref(0)
  const searchQueryHONA = ref('')
  const sortByHONA = ref('id')
  const isSortDirDescHONA = ref(true)
  const startDateFilterHONA = ref('')
  const endDateFilterHONA = ref('')

  // HO - Finished
  const perPageFinishedHO = ref(10)
  const currentPageFinishedHO = ref(1)
  const totalPurchaseRequestsFinishedHO = ref(0)
  const searchQueryFinishedHO = ref('')
  const startDateFilterFinishedHO = ref('')
  const endDateFilterFinishedHO = ref('')

  // Void
  const perPageVoid = ref(10)
  const currentPageVoid = ref(1)
  const totalPurchaseRequestsVoid = ref(0)
  const searchQueryVoid = ref('')
  const startDateFilterVoid = ref('')
  const endDateFilterVoid = ref('')

  // Loading
  const loadingAssetsFGS = ref(false)
  const loadingAssetsFGSNA = ref(false)
  const loadingFinishedAssetsFGS = ref(false)
  const loadingSupplies = ref(false)
  const loadingSuppliesNA = ref(false)
  const loadingFinishedSupplies = ref(false)
  const loadingHO = ref(false)
  const loadingHONA = ref(false)
  const loadingFinishedHO = ref(false)
  const loadingVoid = ref(false)

  // ------------------------------------------------
  // HELPER: Pagination Meta
  // ------------------------------------------------
  const buildMeta = (tableRef, current, perPage, total) => {
    const localCount = tableRef.value ? tableRef.value.localItems.length : 0
    return {
      from: perPage.value * (current.value - 1) + (localCount ? 1 : 0),
      to: perPage.value * (current.value - 1) + localCount,
      of: total.value,
    }
  }

  const dataMeta = computed(() => {
    const af = buildMeta(refPurchaseRequestListTableAssetsFGS, currentPageAssetsFGS, perPageAssetsFGS, totalPurchaseRequestsAssetsFGS)
    const afna = buildMeta(refPurchaseRequestListTableAssetsFGSNA, currentPageAssetsFGSNA, perPageAssetsFGSNA, totalPurchaseRequestsAssetsFGSNA)
    const sup = buildMeta(refPurchaseRequestListTableSupplies, currentPageSupplies, perPageSupplies, totalPurchaseRequestsSupplies)
    const supna = buildMeta(refPurchaseRequestListTableSuppliesNA, currentPageSuppliesNA, perPageSuppliesNA, totalPurchaseRequestsSuppliesNA)
    const ho = buildMeta(refPurchaseRequestListTableHO, currentPageHO, perPageHO, totalPurchaseRequestsHO)
    const hona = buildMeta(refPurchaseRequestListTableHONA, currentPageHONA, perPageHONA, totalPurchaseRequestsHONA)
    const voidMeta = buildMeta(refPurchaseRequestListTableVoid, currentPageVoid, perPageVoid, totalPurchaseRequestsVoid)

    return {
      fromAssetsFGS: af.from,
toAssetsFGS: af.to,
ofAssetsFGS: af.of,
      fromAssetsFGSNA: afna.from,
toAssetsFGSNA: afna.to,
ofAssetsFGSNA: afna.of,
      fromSupplies: sup.from,
toSupplies: sup.to,
ofSupplies: sup.of,
      fromSuppliesNA: supna.from,
toSuppliesNA: supna.to,
ofSuppliesNA: supna.of,
      fromHO: ho.from,
toHO: ho.to,
ofHO: ho.of,
      fromHONA: hona.from,
toHONA: hona.to,
ofHONA: hona.of,
      fromVoid: voidMeta.from,
toVoid: voidMeta.to,
ofVoid: voidMeta.of,
    }
  })

  const dataMetaFinished = computed(() => {
    const Faf = buildMeta(refPurchaseRequestListTableFinishedAssetsFGS, currentPageFinishedAssetsFGS, perPageFinishedAssetsFGS, totalPurchaseRequestsFinishedAssetsFGS)
    const Fsup = buildMeta(refPurchaseRequestListTableFinishedSupplies, currentPageFinishedSupplies, perPageFinishedSupplies, totalPurchaseRequestsFinishedSupplies)
    const Fho = buildMeta(refPurchaseRequestListTableFinishedHO, currentPageFinishedHO, perPageFinishedHO, totalPurchaseRequestsFinishedHO)
    const voidMeta = buildMeta(refPurchaseRequestListTableVoid, currentPageVoid, perPageVoid, totalPurchaseRequestsVoid)

    return {
      fromAssetsFGS: Faf.from,
toAssetsFGS: Faf.to,
ofAssetsFGS: Faf.of,
      fromSupplies: Fsup.from,
toSupplies: Fsup.to,
ofSupplies: Fsup.of,
      fromHO: Fho.from,
toHO: Fho.to,
ofHO: Fho.of,
      fromVoid: voidMeta.from,
toVoid: voidMeta.to,
ofVoid: voidMeta.of,
    }
  })
// ------------------------------------------------
  // REFETCH METHODS
  // ------------------------------------------------
  const refetchDataAssetsFGS = () => {
    if (refPurchaseRequestListTableAssetsFGS.value) refPurchaseRequestListTableAssetsFGS.value.refresh()
  }
  const refetchDataAssetsFGSNA = () => {
    if (refPurchaseRequestListTableAssetsFGSNA.value) refPurchaseRequestListTableAssetsFGSNA.value.refresh()
  }
  const refetchDataFinishedAssetsFGS = () => {
    if (refPurchaseRequestListTableFinishedAssetsFGS.value) refPurchaseRequestListTableFinishedAssetsFGS.value.refresh()
  }
  const refetchDataSupplies = () => {
    if (refPurchaseRequestListTableSupplies.value) refPurchaseRequestListTableSupplies.value.refresh()
  }
  const refetchDataSuppliesNA = () => {
    if (refPurchaseRequestListTableSuppliesNA.value) refPurchaseRequestListTableSuppliesNA.value.refresh()
  }
  const refetchDataFinishedSupplies = () => {
    if (refPurchaseRequestListTableFinishedSupplies.value) refPurchaseRequestListTableFinishedSupplies.value.refresh()
  }
  const refetchDataHO = () => {
    if (refPurchaseRequestListTableHO.value) refPurchaseRequestListTableHO.value.refresh()
  }
  const refetchDataHONA = () => {
    if (refPurchaseRequestListTableHONA.value) refPurchaseRequestListTableHONA.value.refresh()
  }
  const refetchDataFinishedHO = () => {
    if (refPurchaseRequestListTableFinishedHO.value) refPurchaseRequestListTableFinishedHO.value.refresh()
  }
  const refetchDataVoid = () => {
    if (refPurchaseRequestListTableVoid.value) refPurchaseRequestListTableVoid.value.refresh()
  }

  // Attachment filter applies to every tab: back to page 1 and reload mounted tables.
  watch(fileFilter, () => {
    [currentPageAssetsFGS, currentPageAssetsFGSNA, currentPageFinishedAssetsFGS,
      currentPageSupplies, currentPageSuppliesNA, currentPageFinishedSupplies,
      currentPageHO, currentPageHONA, currentPageFinishedHO, currentPageVoid,
    ].forEach(pg => { pg.value = 1 })
    refetchDataAssetsFGS(); refetchDataAssetsFGSNA(); refetchDataFinishedAssetsFGS()
    refetchDataSupplies(); refetchDataSuppliesNA(); refetchDataFinishedSupplies()
    refetchDataHO(); refetchDataHONA(); refetchDataFinishedHO(); refetchDataVoid()
  })

  // ------------------------------------------------
  // WATCHERS (Grouped by Table)
  // ------------------------------------------------
  watch([currentPageAssetsFGS, perPageAssetsFGS, searchQueryAssetsFGS, sortByAssetsFGS, isSortDirDescAssetsFGS, startDateFilterAssetsFGS, endDateFilterAssetsFGS], refetchDataAssetsFGS)
  watch([currentPageAssetsFGSNA, perPageAssetsFGSNA, searchQueryAssetsFGSNA, sortByAssetsFGSNA, isSortDirDescAssetsFGSNA, startDateFilterAssetsFGSNA, endDateFilterAssetsFGSNA], refetchDataAssetsFGSNA)
  watch([currentPageFinishedAssetsFGS, perPageFinishedAssetsFGS, searchQueryFinishedAssetsFGS, startDateFilterFinishedAssetsFGS, endDateFilterFinishedAssetsFGS], refetchDataFinishedAssetsFGS)

  watch([currentPageSupplies, perPageSupplies, searchQuerySupplies, sortBySupplies, isSortDirDescSupplies, startDateFilterSupplies, endDateFilterSupplies], refetchDataSupplies)
  watch([currentPageSuppliesNA, perPageSuppliesNA, searchQuerySuppliesNA, sortBySuppliesNA, isSortDirDescSuppliesNA, startDateFilterSuppliesNA, endDateFilterSuppliesNA], refetchDataSuppliesNA)
  watch([currentPageFinishedSupplies, perPageFinishedSupplies, searchQueryFinishedSupplies, startDateFilterFinishedSupplies, endDateFilterFinishedSupplies], refetchDataFinishedSupplies)

  watch([currentPageHO, perPageHO, searchQueryHO, sortByHO, isSortDirDescHO, startDateFilterHO, endDateFilterHO], refetchDataHO)
  watch([currentPageHONA, perPageHONA, searchQueryHONA, sortByHONA, isSortDirDescHONA, startDateFilterHONA, endDateFilterHONA], refetchDataHONA)
  watch([currentPageFinishedHO, perPageFinishedHO, searchQueryFinishedHO, startDateFilterFinishedHO, endDateFilterFinishedHO], refetchDataFinishedHO)

  watch([currentPageVoid, perPageVoid, searchQueryVoid, startDateFilterVoid, endDateFilterVoid], refetchDataVoid)

  // ------------------------------------------------
  // HELPER: Fetch Error Handler
  // ------------------------------------------------
  const handleFetchError = e => {
    if (e.message === 'Request failed with status code 401') {
      router.push({ name: 'auth-login' })
    } else {
      toast({
        component: ToastificationContent,
        props: { title: 'Error fetching data list', icon: 'AlertTriangleIcon', variant: 'danger' },
      })
    }
  }

  // ------------------------------------------------
  // FETCHERS
  // ------------------------------------------------
  const fetchPurchaseRequestsAssetsFGS = (ctx, callback) => {
    loadingAssetsFGS.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageAssetsFGS.value,
        offset: perPageAssetsFGS.value * (currentPageAssetsFGS.value - 1),
        date_start: startDateFilterAssetsFGS.value || undefined,
        date_end: endDateFilterAssetsFGS.value || undefined,
        search: searchQueryAssetsFGS.value || undefined,
        category: 'Assets',
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsAssetsFGS.value = response.data.data_count
        callback(response.data.data)
        loadingAssetsFGS.value = false
      })
      .catch(e => {
        loadingAssetsFGS.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsAssetsFGSNA = (ctx, callback) => {
    loadingAssetsFGSNA.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageAssetsFGSNA.value,
        offset: perPageAssetsFGSNA.value * (currentPageAssetsFGSNA.value - 1),
        date_start: startDateFilterAssetsFGSNA.value || undefined,
        date_end: endDateFilterAssetsFGSNA.value || undefined,
        search: searchQueryAssetsFGSNA.value || undefined,
        category: 'Assets',
        needs_approval: true,
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsAssetsFGSNA.value = response.data.data_count
        callback(response.data.data)
        loadingAssetsFGSNA.value = false
      })
      .catch(e => {
        loadingAssetsFGSNA.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsFinishedAssetsFGS = (ctx, callback) => {
    loadingFinishedAssetsFGS.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageFinishedAssetsFGS.value,
        offset: perPageFinishedAssetsFGS.value * (currentPageFinishedAssetsFGS.value - 1),
        is_finished: true,
        date_start: startDateFilterFinishedAssetsFGS.value || undefined,
        date_end: endDateFilterFinishedAssetsFGS.value || undefined,
        search: searchQueryFinishedAssetsFGS.value || undefined,
        category: 'Assets',
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsFinishedAssetsFGS.value = response.data.data_count
        callback(response.data.data)
        loadingFinishedAssetsFGS.value = false
      })
      .catch(e => {
        loadingFinishedAssetsFGS.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsSupplies = (ctx, callback) => {
    loadingSupplies.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageSupplies.value,
        offset: perPageSupplies.value * (currentPageSupplies.value - 1),
        date_start: startDateFilterSupplies.value || undefined,
        date_end: endDateFilterSupplies.value || undefined,
        search: searchQuerySupplies.value || undefined,
        category: 'Supplies',
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsSupplies.value = response.data.data_count
        callback(response.data.data)
        loadingSupplies.value = false
      })
      .catch(e => {
        loadingSupplies.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsSuppliesNA = (ctx, callback) => {
    loadingSuppliesNA.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageSuppliesNA.value,
        offset: perPageSuppliesNA.value * (currentPageSuppliesNA.value - 1),
        date_start: startDateFilterSuppliesNA.value || undefined,
        date_end: endDateFilterSuppliesNA.value || undefined,
        search: searchQuerySuppliesNA.value || undefined,
        category: 'Supplies',
        needs_approval: true,
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsSuppliesNA.value = response.data.data_count
        callback(response.data.data)
        loadingSuppliesNA.value = false
      })
      .catch(e => {
        loadingSuppliesNA.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsFinishedSupplies = (ctx, callback) => {
    loadingFinishedSupplies.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageFinishedSupplies.value,
        offset: perPageFinishedSupplies.value * (currentPageFinishedSupplies.value - 1),
        is_finished: true,
        date_start: startDateFilterFinishedSupplies.value || undefined,
        date_end: endDateFilterFinishedSupplies.value || undefined,
        search: searchQueryFinishedSupplies.value || undefined,
        category: 'Supplies',
        type: 'local',
      },
    })
      .then(response => {
        totalPurchaseRequestsFinishedSupplies.value = response.data.data_count
        callback(response.data.data)
        loadingFinishedSupplies.value = false
      })
      .catch(e => {
        loadingFinishedSupplies.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsHO = (ctx, callback) => {
    loadingHO.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageHO.value,
        offset: perPageHO.value * (currentPageHO.value - 1),
        date_start: startDateFilterHO.value || undefined,
        date_end: endDateFilterHO.value || undefined,
        search: searchQueryHO.value || undefined,
        type: 'center',
      },
    })
      .then(response => {
        totalPurchaseRequestsHO.value = response.data.data_count
        callback(response.data.data)
        loadingHO.value = false
      })
      .catch(e => {
        loadingHO.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsHONA = (ctx, callback) => {
    loadingHONA.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageHONA.value,
        offset: perPageHONA.value * (currentPageHONA.value - 1),
        date_start: startDateFilterHONA.value || undefined,
        date_end: endDateFilterHONA.value || undefined,
        search: searchQueryHONA.value || undefined,
        needs_approval: true,
        type: 'center',
      },
    })
      .then(response => {
        totalPurchaseRequestsHONA.value = response.data.data_count
        callback(response.data.data)
        loadingHONA.value = false
      })
      .catch(e => {
        loadingHONA.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsFinishedHO = (ctx, callback) => {
    loadingFinishedHO.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageFinishedHO.value,
        offset: perPageFinishedHO.value * (currentPageFinishedHO.value - 1),
        is_finished: true,
        date_start: startDateFilterFinishedHO.value || undefined,
        date_end: endDateFilterFinishedHO.value || undefined,
        search: searchQueryFinishedHO.value || undefined,
        type: 'center',
      },
    })
      .then(response => {
        totalPurchaseRequestsFinishedHO.value = response.data.data_count
        callback(response.data.data)
        loadingFinishedHO.value = false
      })
      .catch(e => {
        loadingFinishedHO.value = false
        handleFetchError(e)
        callback([])
      })
  }

  const fetchPurchaseRequestsVoid = (ctx, callback) => {
    loadingVoid.value = true
    axios.get(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUESTS}`, {
      headers: { Authorization: `Bearer ${Vue.$cookies.get('userToken')}` },
      params: {
        file_filter: fileFilter.value,
        limit: perPageVoid.value,
        offset: perPageVoid.value * (currentPageVoid.value - 1),
        date_start: startDateFilterVoid.value || undefined,
        date_end: endDateFilterVoid.value || undefined,
        search: searchQueryVoid.value || undefined,
        void: true,
      },
    })
      .then(response => {
        totalPurchaseRequestsVoid.value = response.data.data_count
        callback(response.data.data)
        loadingVoid.value = false
      })
      .catch(e => {
        loadingVoid.value = false
        handleFetchError(e)
        callback([])
      })
  }

  // ------------------------------------------------
  // UI HELPERS
  // ------------------------------------------------
  const resolveUserRoleVariant = role => {
    if (role === 'subscriber') return 'primary'
    if (role === 'author') return 'warning'
    if (role === 'maintainer') return 'success'
    if (role === 'editor') return 'info'
    if (role === 'super-admin') return 'danger'
    return 'primary'
  }

  const resolveUserRoleIcon = role => {
    if (role === 'subscriber') return 'UserIcon'
    if (role === 'author') return 'SettingsIcon'
    if (role === 'maintainer') return 'DatabaseIcon'
    if (role === 'editor') return 'Edit2Icon'
    if (role === 'super-admin') return 'ServerIcon'
    return 'UserIcon'
  }

  const resolveUserStatusVariant = status => {
    if (status === 'pending') return 'warning'
    if (status === 'active') return 'success'
    if (status === 'inactive') return 'secondary'
    return 'primary'
  }

  // RETURN EXPORTS
  return {
    fetchPurchaseRequestsAssetsFGS,
    fetchPurchaseRequestsAssetsFGSNA,
    fetchPurchaseRequestsFinishedAssetsFGS,
    fetchPurchaseRequestsSupplies,
    fetchPurchaseRequestsSuppliesNA,
    fetchPurchaseRequestsFinishedSupplies,
    fetchPurchaseRequestsHO,
    fetchPurchaseRequestsHONA,
    fetchPurchaseRequestsFinishedHO,
    fetchPurchaseRequestsVoid,
    perPageOptions,
    tableColumnsAssetsFGS,
    tableColumnsFinishedAssetsFGS,
    tableColumnsSupplies,
    tableColumnsFinishedSupplies,
    tableColumnsHO,
    tableColumnsFinishedHO,
    tableColumnsVoid,
    perPageAssetsFGS,
    perPageFinishedAssetsFGS,
    totalPurchaseRequestsAssetsFGS,
    totalPurchaseRequestsFinishedAssetsFGS,
    currentPageAssetsFGS,
    currentPageFinishedAssetsFGS,
    perPageSupplies,
    perPageFinishedSupplies,
    totalPurchaseRequestsSupplies,
    totalPurchaseRequestsFinishedSupplies,
    currentPageSupplies,
    currentPageFinishedSupplies,
    perPageHO,
    perPageFinishedHO,
    perPageVoid,
    totalPurchaseRequestsHO,
    totalPurchaseRequestsFinishedHO,
    totalPurchaseRequestsVoid,
    currentPageHO,
    currentPageFinishedHO,
    currentPageVoid,
    searchQueryAssetsFGS,
    searchQueryFinishedAssetsFGS,
    sortByAssetsFGS,
    isSortDirDescAssetsFGS,
    startDateFilterAssetsFGS,
    endDateFilterAssetsFGS,
    startDateFilterFinishedAssetsFGS,
    endDateFilterFinishedAssetsFGS,
    searchQuerySupplies,
    searchQueryFinishedSupplies,
    sortBySupplies,
    isSortDirDescSupplies,
    startDateFilterSupplies,
    endDateFilterSupplies,
    startDateFilterFinishedSupplies,
    endDateFilterFinishedSupplies,
    searchQueryHO,
    searchQueryFinishedHO,
    searchQueryVoid,
    sortByHO,
    isSortDirDescHO,
    startDateFilterHO,
    endDateFilterHO,
    startDateFilterFinishedHO,
    endDateFilterFinishedHO,
    startDateFilterVoid,
    endDateFilterVoid,
    dataMeta,
    dataMetaFinished,
    tableColumnsFinished,
    tableColumnsAssetsFGSNA,
    tableColumnsSuppliesNA,
    tableColumnsHONA,
    perPageAssetsFGSNA,
    totalPurchaseRequestsAssetsFGSNA,
    currentPageAssetsFGSNA,
    perPageSuppliesNA,
    totalPurchaseRequestsSuppliesNA,
    currentPageSuppliesNA,
    perPageHONA,
    totalPurchaseRequestsHONA,
    currentPageHONA,
    searchQueryAssetsFGSNA,
    sortByAssetsFGSNA,
    isSortDirDescAssetsFGSNA,
    startDateFilterAssetsFGSNA,
    endDateFilterAssetsFGSNA,
    searchQuerySuppliesNA,
    sortBySuppliesNA,
    isSortDirDescSuppliesNA,
    startDateFilterSuppliesNA,
    endDateFilterSuppliesNA,
    searchQueryHONA,
    sortByHONA,
    isSortDirDescHONA,
    startDateFilterHONA,
    endDateFilterHONA,
    refPurchaseRequestListTableAssetsFGS,
    refPurchaseRequestListTableSupplies,
    refPurchaseRequestListTableHO,
    refPurchaseRequestListTableFinishedAssetsFGS,
    refPurchaseRequestListTableFinishedSupplies,
    refPurchaseRequestListTableFinishedHO,
    refPurchaseRequestListTableVoid,
    refPurchaseRequestListTableAssetsFGSNA,
    refPurchaseRequestListTableSuppliesNA,
    refPurchaseRequestListTableHONA,
    refetchDataAssetsFGS,
    refetchDataFinishedAssetsFGS,
    refetchDataSupplies,
    refetchDataFinishedSupplies,
    refetchDataHO,
    refetchDataFinishedHO,
    refetchDataVoid,
    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    loadingAssetsFGS,
    loadingAssetsFGSNA,
    loadingFinishedAssetsFGS,
    loadingSupplies,
    loadingSuppliesNA,
    loadingFinishedSupplies,
    loadingHO,
    loadingHONA,
    loadingFinishedHO,
    loadingVoid,
    fileFilter,
  }
}
